import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  AccessTimeRounded,
  LockOutlined,
  LogoutRounded,
  MailOutlineRounded,
  SmsRounded,
  ShieldRounded,
  WhatsApp,
} from '@mui/icons-material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import PortalSwitcherButton from 'src/supportPortal/components/header/PortalSwitcherButton'
import {
  getSupportTwoFactorStatus,
  resendSupportTwoFactorChallenge,
  startSupportTwoFactorChallenge,
  verifySupportTwoFactorChallenge,
} from 'src/supportPortal/services/supportTwoFactorService'
import { logoutHandlerSupport } from 'src/supportPortal/utility/supportGeneralUtils'
import { getSupportTwoFactorRequiredEventName } from 'src/services/axiosInterceptor'

function getErrorMessage(error, fallback) {
  const retryAfterSeconds =
    error?.response?.data?.data?.retryAfterSeconds || error?.response?.data?.retryAfterSeconds
  if (retryAfterSeconds > 0) {
    return `Please wait ${retryAfterSeconds} seconds before requesting another code.`
  }
  return error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback
}

function formatMethodLabel(method) {
  switch (method) {
    case 'EMAIL':
      return 'Verified email'
    case 'WHATSAPP':
      return 'Verified WhatsApp'
    case 'MOBILE':
      return 'Verified mobile'
    default:
      return method || 'Verified contact'
  }
}

function getMethodIcon(method) {
  switch (method) {
    case 'EMAIL':
      return <MailOutlineRounded fontSize="small" />
    case 'WHATSAPP':
      return <WhatsApp fontSize="small" />
    case 'MOBILE':
      return <SmsRounded fontSize="small" />
    default:
      return <ShieldRounded fontSize="small" />
  }
}

function getMaskedDestinationForMethod(status, method) {
  if (!status) {
    return ''
  }
  const destinations = status.methodDestinations || {}
  if (method && destinations[method]) {
    return destinations[method]
  }
  if (status.currentMethod && destinations[status.currentMethod]) {
    return destinations[status.currentMethod]
  }
  if (status.preferredMethod && destinations[status.preferredMethod]) {
    return destinations[status.preferredMethod]
  }
  return status.maskedDestination || ''
}

function getExpiryTimeMs(expiresAt) {
  if (!expiresAt) {
    return null
  }
  const parsedTime =
    typeof expiresAt === 'number'
      ? (expiresAt > 1e12 ? expiresAt : expiresAt * 1000)
      : new Date(expiresAt).getTime()
  return Number.isNaN(parsedTime) ? null : parsedTime
}

function formatTimeRemaining(expiresAt, nowMs = Date.now()) {
  const parsedTime = getExpiryTimeMs(expiresAt)
  if (!parsedTime) {
    return { label: null, shortLabel: null, expired: false }
  }
  const diffMs = parsedTime - nowMs
  if (diffMs <= 0) {
    return { label: 'Code expired', shortLabel: '0:00', expired: true }
  }
  const totalSeconds = Math.floor(diffMs / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  const shortLabel = `${minutes}:${`${seconds}`.padStart(2, '0')}`
  return { label: `${shortLabel} remaining`, shortLabel, expired: false }
}

function getMethodSummary(method) {
  switch (method) {
    case 'EMAIL':
      return 'Email'
    case 'WHATSAPP':
      return 'WhatsApp'
    case 'MOBILE':
      return 'Mobile'
    default:
      return 'Contact'
  }
}

function getDestinationSummary(status, method) {
  return `${getMethodSummary(method)}: ${getMaskedDestinationForMethod(status, method) || 'Available'}`
}

function MethodBadge({ method }) {
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.9,
        borderRadius: 999,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        color: '#1d4ed8',
        background: 'rgba(239, 246, 255, 0.98)',
        border: '1px solid rgba(37, 99, 235, 0.24)',
      }}
    >
      {getMethodIcon(method)}
      <Typography variant="body2" sx={{ fontWeight: 700, color: 'inherit' }}>
        {formatMethodLabel(method)}
      </Typography>
    </Box>
  )
}

function SupportTwoFactorPrompt({
  status,
  otp,
  selectedMethod,
  onOtpChange,
  onMethodChange,
  onSendCode,
  onVerify,
  onLogout,
  loading,
  errorMessage,
  infoMessage,
  resendAvailableAt,
  modal = false,
}) {
  const hasDestinations = Array.isArray(status?.allowedMethods) && status.allowedMethods.length > 0
  const hasMultipleMethods = (status?.allowedMethods?.length || 0) > 1
  const destinationLabel = getMaskedDestinationForMethod(status, selectedMethod)
  const challengeExpiresAtMs = getExpiryTimeMs(status?.challengeExpiresAt)
  const resendAvailableAtMs = getExpiryTimeMs(resendAvailableAt)
  const [nowMs, setNowMs] = useState(Date.now())

  useEffect(() => {
    setNowMs(Date.now())
    if ((!challengeExpiresAtMs || !status?.challengeActive) && !resendAvailableAtMs) {
      return undefined
    }
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now())
    }, 1000)
    return () => window.clearInterval(intervalId)
  }, [challengeExpiresAtMs, resendAvailableAtMs, status?.challengeActive])

  const challengeCountdown = formatTimeRemaining(status?.challengeExpiresAt, nowMs)
  const resendCountdown = formatTimeRemaining(resendAvailableAtMs, nowMs)
  const isCodeExpired = challengeCountdown.expired
  const isResendLocked = Boolean(resendAvailableAtMs && !resendCountdown.expired)
  const title = modal ? 'Reconfirm support access' : 'Confirm your support access'
  const subtitle = modal
    ? 'Your support session needs a quick recheck before you continue.'
    : 'Enter the one-time code to continue using support tools.'
  const primaryActionLabel = isResendLocked
    ? `Resend in ${resendCountdown.shortLabel}`
    : isCodeExpired || status?.challengeActive
      ? 'Resend code'
      : 'Send code'
  const timerBadgeLabel = isResendLocked
    ? `Resend in ${resendCountdown.shortLabel}`
    : challengeCountdown.label || 'Ready to send'

  const content = (
    <Paper
      elevation={modal ? 0 : 8}
      sx={{
        width: '100%',
        maxWidth: 560,
        maxHeight: { xs: 'calc(100vh - 20px)', sm: 'calc(100vh - 48px)' },
        borderRadius: 6,
        p: { xs: 2, sm: 4 },
        border: '1px solid rgba(148, 163, 184, 0.28)',
        background:
          'linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.95))',
        backdropFilter: 'blur(24px) saturate(150%)',
        boxShadow: '0 30px 90px rgba(15, 23, 42, 0.34)',
        overflowX: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at top right, rgba(96, 165, 250, 0.18), transparent 34%), radial-gradient(circle at bottom left, rgba(139, 92, 246, 0.16), transparent 28%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Button
        variant="text"
        size="small"
        onClick={onLogout}
        startIcon={<LogoutRounded fontSize="small" />}
        sx={{
          position: 'absolute',
          top: { xs: 12, sm: 16 },
          right: { xs: 12, sm: 16 },
          zIndex: 2,
          textTransform: 'none',
          borderRadius: 999,
          px: 1.5,
          py: 0.75,
          minWidth: 'auto',
          fontWeight: 700,
          color: '#102a43',
          background: '#ffffff',
          border: '1px solid rgba(148, 163, 184, 0.4)',
          boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
          '&:hover': {
            background: '#f8fafc',
            borderColor: 'rgba(37, 99, 235, 0.28)',
          },
        }}
      >
        Logout
      </Button>
      <Stack spacing={{ xs: 2, sm: 3 }} sx={{ position: 'relative', zIndex: 1 }}>
        <Stack direction="row" spacing={{ xs: 1.25, sm: 1.5 }} alignItems="center">
          <Box
            sx={{
              width: { xs: 44, sm: 52 },
              height: { xs: 44, sm: 52 },
              borderRadius: { xs: '14px', sm: '18px' },
              display: 'grid',
              placeItems: 'center',
              color: '#4338ca',
              background: 'linear-gradient(135deg, rgba(224, 231, 255, 0.96), rgba(219, 234, 254, 0.92))',
              border: '1px solid rgba(99, 102, 241, 0.16)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)',
            }}
          >
            <ShieldRounded />
          </Box>
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, color: '#102a43', lineHeight: 1.1, fontSize: { xs: '1.35rem', sm: '1.5rem' } }}
            >
              {title}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.35, color: '#334e68', fontWeight: 500 }}>
              {subtitle}
            </Typography>
          </Box>
        </Stack>

        {errorMessage ? (
          <Alert severity="error" sx={{ borderRadius: 3 }}>
            {errorMessage}
          </Alert>
        ) : null}
        {infoMessage ? (
          <Alert severity="info" sx={{ borderRadius: 3 }}>
            {infoMessage}
          </Alert>
        ) : null}
        {isCodeExpired ? (
          <Alert severity="warning" sx={{ borderRadius: 3 }}>
            Your previous code has expired. Request a fresh code to continue.
          </Alert>
        ) : null}

        {!hasDestinations ? (
          <Alert severity="warning" sx={{ borderRadius: 3 }}>
            No validated second-factor destination is available for this support account yet. A Control Center
            admin needs to set and validate a trusted user-level email or WhatsApp destination before support
            access can continue.
          </Alert>
        ) : (
          <>
            {hasMultipleMethods ? (
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ color: '#243b53', fontWeight: 800 }}>
                  Choose where to send the code
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  {status.allowedMethods.map((method) => {
                    const isSelected = selectedMethod === method
                    return (
                      <Button
                        key={method}
                        variant="text"
                        onClick={() => onMethodChange(method)}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          borderRadius: 3,
                          px: { xs: 1.25, sm: 1.5 },
                          py: { xs: 1, sm: 1.15 },
                          minHeight: { xs: 54, sm: 58 },
                          flex: 1,
                          color: '#102a43',
                          border: isSelected
                            ? '1px solid rgba(37, 99, 235, 0.4)'
                            : '1px solid rgba(148, 163, 184, 0.34)',
                          background: isSelected
                            ? 'linear-gradient(135deg, rgba(219, 234, 254, 0.98), rgba(238, 242, 255, 0.98))'
                            : 'rgba(255,255,255,0.9)',
                          boxShadow: isSelected
                            ? '0 12px 30px rgba(37, 99, 235, 0.16)'
                            : '0 4px 16px rgba(15, 23, 42, 0.05)',
                          '&:hover': {
                            background: isSelected
                              ? 'linear-gradient(135deg, rgba(219, 234, 254, 1), rgba(238, 242, 255, 1))'
                              : '#ffffff',
                            borderColor: isSelected
                              ? 'rgba(37, 99, 235, 0.52)'
                              : 'rgba(100, 116, 139, 0.42)',
                          },
                        }}
                      >
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: '12px',
                              display: 'grid',
                              placeItems: 'center',
                              color: isSelected ? '#1d4ed8' : '#334e68',
                              background: '#ffffff',
                              border: isSelected
                                ? '1px solid rgba(37, 99, 235, 0.16)'
                                : '1px solid rgba(148, 163, 184, 0.24)',
                            }}
                          >
                            {getMethodIcon(method)}
                          </Box>
                          <Box sx={{ textAlign: 'left' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'inherit' }}>
                              {formatMethodLabel(method)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#334e68', fontWeight: 500 }}>
                              {getMaskedDestinationForMethod(status, method) || 'Verified destination'}
                            </Typography>
                          </Box>
                        </Stack>
                      </Button>
                    )
                  })}
                </Stack>
              </Stack>
            ) : (
              <Stack spacing={1} alignItems="flex-start">
                <Typography variant="subtitle2" sx={{ color: '#243b53', fontWeight: 800 }}>
                  Code delivery
                </Typography>
                <MethodBadge method={selectedMethod} />
              </Stack>
            )}

            <Box
              sx={{
                borderRadius: 4,
                px: { xs: 1.5, sm: 2.25 },
                py: { xs: 1.5, sm: 2 },
                background:
                  'linear-gradient(135deg, rgba(30, 41, 99, 0.94), rgba(91, 33, 182, 0.9))',
                color: '#f8fafc',
                boxShadow: '0 18px 40px rgba(15, 23, 42, 0.2)',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                spacing={{ xs: 1.25, sm: 2 }}
              >
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                    {hasMultipleMethods ? 'Selected destination' : 'Code will go to'}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ mt: 0.5, fontWeight: 800, color: '#fff' }}>
                    {destinationLabel || getDestinationSummary(status, selectedMethod)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 999,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    background: 'rgba(255,255,255,0.14)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    whiteSpace: 'nowrap',
                    alignSelf: { xs: 'flex-start', sm: 'auto' },
                  }}
                >
                  <AccessTimeRounded sx={{ fontSize: 16, color: 'rgba(255,255,255,0.82)' }} />
                  <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700 }}>
                    {timerBadgeLabel}
                  </Typography>
                </Box>
              </Stack>
              <Typography variant="body2" sx={{ mt: 1.25, color: 'rgba(255,255,255,0.78)' }}>
                Keep this window open while you confirm the code.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="contained"
                onClick={() => onSendCode(Boolean(status?.challengeActive))}
                disabled={loading || isResendLocked}
                sx={{
                  textTransform: 'none',
                  borderRadius: 999,
                  px: { xs: 2.5, sm: 3 },
                  py: 1.1,
                  fontWeight: 700,
                  letterSpacing: '0.01em',
                  color: '#ffffff',
                  background: 'linear-gradient(135deg, #1d4ed8, #5b21b6)',
                  boxShadow: '0 14px 30px rgba(37, 99, 235, 0.24)',
                  '&:hover': { background: 'linear-gradient(135deg, #1e40af, #4c1d95)' },
                  '&.Mui-disabled': {
                    color: '#f8fafc',
                    background: '#475569',
                    border: '1px solid rgba(51, 65, 85, 0.9)',
                    boxShadow: 'none',
                    opacity: 1,
                  },
                }}
              >
                {primaryActionLabel}
              </Button>
            </Stack>

            <TextField
              fullWidth
              label="One-time code"
              value={otp}
              onChange={(event) => onOtpChange(event.target.value.replace(/\D/g, '').slice(0, 8))}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', autoComplete: 'one-time-code' }}
              helperText={isCodeExpired ? 'Request a fresh code, then enter it here.' : 'Enter the latest code you received.'}
              sx={{
                '& .MuiFormHelperText-root': {
                  mx: 0,
                },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  background: 'rgba(255,255,255,0.96)',
                  backdropFilter: 'blur(12px)',
                  '& input': {
                    color: '#102a43',
                    fontWeight: 600,
                  },
                },
                '& .MuiInputLabel-root': { color: '#334e68' },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      sx={{
                        mr: 0.5,
                        width: 34,
                        height: 34,
                        borderRadius: '12px',
                        display: 'grid',
                        placeItems: 'center',
                        color: '#4f46e5',
                        background: 'rgba(224, 231, 255, 0.9)',
                      }}
                    >
                      <LockOutlined fontSize="small" />
                    </Box>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              size="large"
              onClick={onVerify}
              disabled={loading || otp.trim().length === 0 || isCodeExpired}
              sx={{
                textTransform: 'none',
                borderRadius: 999,
                py: 1.2,
                fontWeight: 800,
                letterSpacing: '0.01em',
                color: '#ffffff',
                background: 'linear-gradient(135deg, #1d4ed8, #5b21b6)',
                boxShadow: '0 18px 40px rgba(37, 99, 235, 0.26)',
                '&:hover': { background: 'linear-gradient(135deg, #1e40af, #4c1d95)' },
                '&.Mui-disabled': {
                  color: '#f8fafc',
                  background: '#475569',
                  border: '1px solid rgba(51, 65, 85, 0.9)',
                  boxShadow: 'none',
                  opacity: 1,
                },
              }}
            >
              {loading ? <CircularProgress color="inherit" size={20} /> : 'Verify and continue'}
            </Button>
          </>
        )}

        {modal ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{
              pt: 0.5,
              borderTop: '1px solid rgba(148, 163, 184, 0.18)',
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ color: '#334e68', fontWeight: 700 }}>
                Need a different portal?
              </Typography>
              <Typography variant="caption" sx={{ color: '#627d98' }}>
                Switch safely without dismissing this security check.
              </Typography>
            </Box>
            <PortalSwitcherButton surface="light" />
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  )

  if (modal) {
    return (
      <Dialog
        open
        onClose={undefined}
        disableEscapeKeyDown
        fullWidth
        maxWidth="sm"
        PaperProps={{
          elevation: 0,
          sx: {
            background: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
            maxHeight: '100%',
          },
        }}
        BackdropProps={{
          sx: {
            background:
              'linear-gradient(180deg, rgba(30, 41, 99, 0.52), rgba(88, 28, 135, 0.66))',
            backdropFilter: 'blur(24px) saturate(120%)',
          },
        }}
      >
        <DialogContent
          sx={{
            p: { xs: 1, sm: 3 },
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
          }}
        >
          {content}
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'center',
        px: 2,
        py: { xs: 1, sm: 4 },
        background:
          'linear-gradient(180deg, rgba(30, 41, 99, 0.58), rgba(88, 28, 135, 0.74))',
        backdropFilter: 'blur(24px) saturate(125%)',
        overflowY: 'auto',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 18% 18%, rgba(96, 165, 250, 0.16), transparent 22%), radial-gradient(circle at 82% 82%, rgba(139, 92, 246, 0.16), transparent 24%)',
          pointerEvents: 'none',
        }}
      />
      {content}
    </Box>
  )
}

export default function SupportTwoFactorGate({ children }) {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [selectedMethod, setSelectedMethod] = useState('')
  const [otp, setOtp] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [infoMessage, setInfoMessage] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [hasEnteredPortal, setHasEnteredPortal] = useState(false)
  const [resendAvailableAt, setResendAvailableAt] = useState(null)
  const hasAutoSentForPromptRef = useRef(false)

  const shouldRequireTwoFactor = Boolean(status?.portalTwoFactorEnforced && status?.userTwoFactorEnabled)
  const isVerified = Boolean(status?.verified)

  const activeMethod = useMemo(
    () => selectedMethod || status?.currentMethod || status?.preferredMethod || status?.allowedMethods?.[0] || '',
    [selectedMethod, status],
  )

  async function refreshStatus({ asModal = false } = {}) {
    const nextStatus = await getSupportTwoFactorStatus()
    const nextResendAvailableAt = getExpiryTimeMs(nextStatus?.resendAvailableAt)
    setStatus(nextStatus)
    setSelectedMethod(nextStatus?.currentMethod || nextStatus?.preferredMethod || nextStatus?.allowedMethods?.[0] || '')
    setResendAvailableAt((previousValue) => {
      if (nextResendAvailableAt) {
        return nextResendAvailableAt
      }
      return nextStatus?.challengeActive ? previousValue : null
    })
    setOtp('')
    setErrorMessage('')
    setInfoMessage('')
    setShowModal(Boolean(asModal && nextStatus?.shouldRequireSupportTwoFactor && !nextStatus?.verified))
    return nextStatus
  }

  useEffect(() => {
    async function loadInitialStatus() {
      setLoading(true)
      try {
        await refreshStatus()
      } catch (error) {
        setErrorMessage(getErrorMessage(error, 'Unable to check support verification right now.'))
      } finally {
        setLoading(false)
      }
    }

    loadInitialStatus()
  }, [])

  useEffect(() => {
    if (!shouldRequireTwoFactor || isVerified) {
      setHasEnteredPortal(true)
    }
  }, [isVerified, shouldRequireTwoFactor])

  useEffect(() => {
    if (!hasEnteredPortal) {
      return
    }
    if (shouldRequireTwoFactor && !isVerified) {
      setShowModal(true)
    }
  }, [hasEnteredPortal, isVerified, shouldRequireTwoFactor])

  useEffect(() => {
    const handleTwoFactorRequired = async () => {
      try {
        await refreshStatus({ asModal: true })
      } catch (error) {
        setErrorMessage(getErrorMessage(error, 'Unable to refresh support verification state.'))
        setShowModal(true)
      }
    }

    const eventName = getSupportTwoFactorRequiredEventName()
    window.addEventListener(eventName, handleTwoFactorRequired)
    return () => window.removeEventListener(eventName, handleTwoFactorRequired)
  }, [])

  useEffect(() => {
    if (!hasEnteredPortal || !shouldRequireTwoFactor) {
      return undefined
    }

    let polling = false

    const syncStatus = async () => {
      if (polling) {
        return
      }
      polling = true
      try {
        await refreshStatus({ asModal: true })
      } catch (error) {
        console.error('Unable to refresh support 2FA status:', error)
      } finally {
        polling = false
      }
    }

    const handleFocus = () => {
      syncStatus()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncStatus()
      }
    }

    const intervalId = window.setInterval(syncStatus, 10 * 60 * 1000)
    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.clearInterval(intervalId)
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [hasEnteredPortal, shouldRequireTwoFactor])

  const isPromptVisible =
    (shouldRequireTwoFactor && !isVerified && !hasEnteredPortal) ||
    (showModal && shouldRequireTwoFactor && !isVerified)

  useEffect(() => {
    if (!isPromptVisible) {
      hasAutoSentForPromptRef.current = false
    }
  }, [isPromptVisible])

  useEffect(() => {
    if (
      !isPromptVisible ||
      loading ||
      submitting ||
      !activeMethod ||
      !Array.isArray(status?.allowedMethods) ||
      status.allowedMethods.length === 0
    ) {
      return
    }

    if (resendAvailableAt && resendAvailableAt > Date.now()) {
      hasAutoSentForPromptRef.current = true
      return
    }

    if (hasAutoSentForPromptRef.current) {
      return
    }

    hasAutoSentForPromptRef.current = true
    void handleSendCode(Boolean(status?.challengeActive))
  }, [
    activeMethod,
    isPromptVisible,
    loading,
    resendAvailableAt,
    status?.allowedMethods,
    status?.challengeActive,
    submitting,
  ])

  async function handleSendCode(isResend = false) {
    if (!activeMethod) {
      setErrorMessage('Please choose a verified destination first.')
      return
    }

    setSubmitting(true)
    setErrorMessage('')
    setInfoMessage('')

    try {
      setOtp('')
      const nextStatus = isResend
        ? await resendSupportTwoFactorChallenge(activeMethod)
        : await startSupportTwoFactorChallenge(activeMethod)
      const nextResendAvailableAt = getExpiryTimeMs(nextStatus?.resendAvailableAt)
      setStatus(nextStatus)
      setSelectedMethod(nextStatus?.currentMethod || activeMethod)
      setResendAvailableAt(nextResendAvailableAt)
      const nextMethod = nextStatus?.currentMethod || activeMethod
      setInfoMessage(`We sent a code to ${getMaskedDestinationForMethod(nextStatus, nextMethod) || 'your verified contact'}.`)
    } catch (error) {
      const nextResendAvailableAt =
        getExpiryTimeMs(error?.response?.data?.data?.resendAvailableAt) ||
        getExpiryTimeMs(error?.response?.data?.resendAvailableAt)
      if (nextResendAvailableAt) {
        setResendAvailableAt(nextResendAvailableAt)
      }
      setErrorMessage(getErrorMessage(error, 'Unable to send a support verification code right now.'))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleVerify() {
    if (!otp.trim()) {
      setErrorMessage('Please enter the one-time code.')
      return
    }

    setSubmitting(true)
    setErrorMessage('')
    setInfoMessage('')

    try {
      const nextStatus = await verifySupportTwoFactorChallenge(otp.trim())
      setStatus(nextStatus)
      setSelectedMethod(nextStatus?.currentMethod || activeMethod)
      setResendAvailableAt(null)
      setOtp('')
      if (nextStatus?.verified) {
        setShowModal(false)
        toast.success('Support access confirmed.')
      }
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'Unable to verify the one-time code.'))
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (status == null) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Alert severity="error">Unable to initialize support verification right now.</Alert>
      </Box>
    )
  }

  const promptProps = {
    status,
    otp,
    selectedMethod: activeMethod,
    onOtpChange: setOtp,
    onMethodChange: (method) => {
      setSelectedMethod(method)
      setInfoMessage('')
      setErrorMessage('')
    },
    onSendCode: handleSendCode,
    onVerify: handleVerify,
    onLogout: () => {
      logoutHandlerSupport()
      window.location.assign('/support-home/login')
    },
    loading: submitting,
    errorMessage,
    infoMessage,
    resendAvailableAt,
  }

  const shouldBlockPortal = shouldRequireTwoFactor && !isVerified && !hasEnteredPortal

  if (shouldBlockPortal) {
    return <SupportTwoFactorPrompt {...promptProps} />
  }

  return (
    <>
      {children}
      {showModal && shouldRequireTwoFactor && !isVerified ? <SupportTwoFactorPrompt {...promptProps} modal /> : null}
    </>
  )
}
